import React, { useCallback, useEffect, useMemo } from 'react';
import {
  func, node,
} from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import every from 'lodash/every';
import find from 'lodash/find';
import omit from 'lodash/omit';
import { useSelector, useDispatch } from 'react-redux';
import {
  objectFilter, getDeviceType, autoScrollToTheErrorField,
} from '../../shared/utils/commonUtils';
import CheckoutView from './components/CheckoutView';
import {
  Container, ContainerTitle, ContainerBody, ContainerContent, ContainerContentCenter, ContainerContentBar,
} from '../common/layouts';
import transformAnalyticsData from './utils/transformAnalyticsData';
import {
  setEnvParams, setDefaultData, setOrderInfo, setUserInfo, setOrderPaymentResult,
} from './checkoutSlice';
import useOfferWidgets from '../common/Offers/useOfferWidgets';
import {
  useLazyInitThenStartCheckoutQuery,
  useLazyContinueCheckoutQuery,
  useLazyGetOrderInfoQuery,
  useLazyGetAnalyticsInfoQuery,
  useCheckoutNowMutation,
} from '../../store/services/checkout/checkoutApi';
import { useLazyGetUserInfoQuery } from '../../store/services/common/userInfo/userInfoApi';
import { removeTransToken, TRANS_TOKEN_SESSION_ITEM_NAME_SUFFIX } from '../../store/utils/handleTokens';
import pubSubInstance from '../../shared/utils/pubSub';
import { SHOW_ERROR_ONLY_CODES } from '../../store/utils/handleErrors';
import OrderSummaryContainer from './components/OrderSummaryContainer';
import PaymentResultContainer from '../common/PaymentResultContainer';
import { setErrorMessage, setErrorCode } from '../../store/slices/errorSlice';
import { showLoadder, hideLoadder } from '../../store/slices/loadderSlice';
import fireLinkTrust from './utils/fireLinkTrust';
import Make3ds from '../common/Make3ds';
import {
  setSubmitParams, setMake3dsFlow, MAKE3DSFLOW,
} from '../common/Make3ds/make3dsSlice';
import usePassportWidgets from '../common/passportWidgets/utils/usePassportWidgets';
import { setLatestBillingContact } from '../common/Payment/components/PaymentMethods/paymentMethodsSlice';

const Checkout = ({
  trackingCallback, orderReviewActionCallback, handleOrderReviewCallback,
  onUserLoginAction, handleInvalidTransactionAction, checkoutCallback,
}) => {
  const dispatch = useDispatch();
  const {
    isPaymentFlowTypeSwitched, dataOf3ds: { md = '{}' } = {},
    locale, reservationGroupId, jwt, checkoutOrderIdentifier,
    currentPaymentFlowType,
    orderReviewModel,
    widgetNameSpace,
    isShowConfirmationSection,
    confirmationMessage: {
      title,
      isShowConfirmPageThanksSection = true,
    } = {},
    SwitchPaymentFlowTypeSection,
    listingName,
    affiliateMarketing: { enableLinkTrust = false } = {},
  } = useSelector(state => state.propsData);
  const {
    entities: {
      envParams: { enablePreviewMode },
      orderInfo,
      orderInfo: { totalAmount },
      orderPaymentResult: { successfulOrBackground },
    },
  } = useSelector(state => state.mainStateData);
  const {
    errorMsgCode,
  } = useSelector(state => state.error);
  const {
    currentPaymentOptionType, useCreditBalance,
    paymentPlanOptions,
  } = useSelector(state => state.paymentOptions);
  const { initOfferWidget, submitOffers } = useOfferWidgets();
  const { initPassportWidgets } = usePassportWidgets();
  const { currentActivePaymentMethod, billingContact: billingContactData } = useSelector(state => state.paymentMethods);
  const [ initThenStartCheckout ] = useLazyInitThenStartCheckoutQuery();
  const [ continueCheckout ] = useLazyContinueCheckoutQuery();
  const [ getOrderInfo ] = useLazyGetOrderInfoQuery();
  const [ getAnalyticsInfo ] = useLazyGetAnalyticsInfoQuery();
  const [ getUserInfo ] = useLazyGetUserInfoQuery();
  const [ checkoutNow ] = useCheckoutNowMutation();
  const { bankRedirectSuccess } = useMemo(() => JSON.parse(md), [ md ]);
  const showErrorMsgOnly = useMemo(() => SHOW_ERROR_ONLY_CODES.includes(errorMsgCode), [ errorMsgCode ]);
  const showContent = useMemo(() => !isEmpty(orderInfo), [ orderInfo ]);
  const isShowConfirmation = useMemo(() => isShowConfirmationSection && successfulOrBackground,
    [ isShowConfirmationSection, successfulOrBackground ]);

  const manuallyShowLoadder = useCallback(() => {
    dispatch(showLoadder());
  }, [ dispatch ]);

  const manullyHideLoadder = useCallback(() => {
    dispatch(hideLoadder());
  }, [ dispatch ]);

  const unMountSwitchPaymentFlow = useCallback(() => {
    !!currentPaymentFlowType && pubSubInstance.dispatch(`${widgetNameSpace}-set-show-switch`, false);
  }, [ currentPaymentFlowType, widgetNameSpace ]);

  const loadOrderData = useCallback(() => {
    getOrderInfo({ params: objectFilter({ orderReviewModel }) }).then(({ data }) => {
      if (data) {
        const { order } = data;
        dispatch(setOrderInfo(order));
      }
    });
  }, [ dispatch, getOrderInfo, orderReviewModel ]);

  const loadAllData = useCallback(() => {
    loadOrderData();
    getAnalyticsInfo().then(({ data }) => {
      if (data) {
        trackingCallback({ id: 'cui.checkout.loaded', data: transformAnalyticsData(data) });
      }
    });
    getUserInfo().then(({ data }) => {
      if (data) {
        dispatch(setUserInfo(data));
        manuallyShowLoadder();
        initPassportWidgets({ passportInfo: data.passportInfo, initPassportCallback: manullyHideLoadder });
      }
    });
  }, [ dispatch, getAnalyticsInfo, getUserInfo, loadOrderData, trackingCallback, initPassportWidgets ]);

  const initResultHandle = useCallback(({ data }) => {
    if (data) {
      const { offerInitInfoResponse, ...restEnvParams } = data;
      initOfferWidget({ offerInitInfoResponse, bankRedirectSuccess, initOfferCallback: manullyHideLoadder });
      dispatch(setEnvParams(restEnvParams));
      if (bankRedirectSuccess) {
        dispatch(setMake3dsFlow(MAKE3DSFLOW.COM3DSFLOW));
      } else {
        loadAllData(true);
      }
    } else {
      unMountSwitchPaymentFlow();
      manullyHideLoadder();
    }
  }, [ dispatch, loadAllData, bankRedirectSuccess, initOfferWidget, manullyHideLoadder, unMountSwitchPaymentFlow ]);

  const initThenStartCheckoutHandler = useCallback(() => {
    const sessionCouponCodes = sessionStorage.getItem(`checkoutCoupon_${checkoutOrderIdentifier}`);
    const params = {
      deviceType: getDeviceType(),
      reservationGroupId,
      locale,
      jwt,
      identifier: checkoutOrderIdentifier,
      // keep coupon for orderReview edit back etc.
      couponCodes: sessionCouponCodes ? JSON.parse(sessionCouponCodes) : undefined,
      paymentFlowType: currentPaymentFlowType,
    };
    initThenStartCheckout({ params }).then(initResultHandle);
  }, [ checkoutOrderIdentifier, locale, jwt, reservationGroupId,
    currentPaymentFlowType, initResultHandle, initThenStartCheckout ]);

  const continueCheckoutHandler = useCallback(() => {
    const continueParams = isPaymentFlowTypeSwitched
      ? { continuePaymentFlowType: currentPaymentFlowType } : undefined;
    continueCheckout({ params: continueParams }).then(initResultHandle);
  }, [ currentPaymentFlowType, isPaymentFlowTypeSwitched, initResultHandle, continueCheckout ]);

  const removeMdData = useCallback(() => {
    sessionStorage.removeItem(`${widgetNameSpace}-checkoutMDData`);
    dispatch(setDefaultData({}));
  }, [ widgetNameSpace, dispatch ]);

  const resetGlobalError = useCallback(() => {
    dispatch(setErrorMessage(''));
    dispatch(setErrorCode(''));
  }, [ dispatch ]);

  const handleReloadData = useCallback((params = {}) => {
    const { isReloadOffer } = params;
    removeMdData();
    resetGlobalError();
    loadOrderData();
    isReloadOffer && pubSubInstance.dispatch(`${widgetNameSpace}-offer-reInit`);
    dispatch(setLatestBillingContact(billingContactData));
  }, [ loadOrderData, widgetNameSpace, removeMdData, resetGlobalError, billingContactData ]);

  const handleValidate = useCallback(() => {
    return new Promise((resolve) => {
      pubSubInstance.dispatch(`${widgetNameSpace}-validate-block`, undefined, (res) => {
        const isValidate = every(res, (value) => { return value === true; });
        resolve(isValidate);
      });
    });
  }, [ widgetNameSpace ]);

  const checkoutService = useCallback((params, isReloadDatas) => {
    getAnalyticsInfo().then(({ data }) => {
      if (data) {
        trackingCallback({ id: 'cui.checkout.beforeCheckout', data: transformAnalyticsData(data) });
      }
    });
    const finalCheckoutParams = {
      ...omit(params, [
        'waiversData',
        'receiptAccountFirstName',
        'receiptAccountLastName',
        'receiptAccountEmail',
      ]),
    };

    checkoutNow({ params: finalCheckoutParams }).then(({ data }) => {
      if (data) {
        checkoutCallback(data);
        dispatch(setOrderPaymentResult(data));
        submitOffers(data);
        enableLinkTrust && fireLinkTrust(data.analyticsResponse?.orderData?.originalOrderId);
        data.analyticsResponse && trackingCallback({ id: 'cui.checkout.complete', data: transformAnalyticsData(data.analyticsResponse) });
      } else {
        !!isReloadDatas && loadAllData();
      }
    }).finally(manullyHideLoadder);
  }, [ checkoutCallback, checkoutNow, dispatch, enableLinkTrust,
    getAnalyticsInfo, loadAllData, trackingCallback, manullyHideLoadder ]);

  const handlePreviewSubmit = useCallback(() => {
    pubSubInstance.dispatch(`${widgetNameSpace}-preview-submit`, undefined, (res) => {
      manullyHideLoadder();
      const haveFalseStatus = find(res, { status: false });
      const { msg, errorCode } = res.reduce((r, v) => ({ ...r, ...v }), {});
      if (!haveFalseStatus) {
        trackingCallback({
          id: 'cui.previewCheckout.triggered',
        });
        checkoutService(objectFilter({
          paymentOptionType: currentPaymentOptionType,
          paymentMethodType: currentActivePaymentMethod,
          useCreditBalance,
          paymentPlanOptions,
          paymentAccountId: currentActivePaymentMethod ? 'PREVIEW_PAYMENT_ACCOUNT_ID' : undefined,
          receiptEmail: 'previewcheckout@active.com',
          billingContact: currentActivePaymentMethod ? {
            firstName: 'PreviewFirstName',
            lastName: 'PreviewLastName',
            email: 'previewcheckout@active.com',
            address: {
              country: 'US',
              postalCode: '66666',
              stateProvince: 'TX',
              city: 'somecity',
              line1: 'somewhere',
              line2: 'somewhere',
            },
          } : undefined,
          stateOf3DS: 'defaultStateOf3DS',
          listingName,
        }));
      } else if (errorCode) {
        dispatch(setErrorMessage({ type: 'danger' }));
      } else if (msg) {
        dispatch(setErrorMessage(msg));
      }
    });
  }, [ currentPaymentOptionType, currentActivePaymentMethod, useCreditBalance,
    paymentPlanOptions, checkoutService, trackingCallback, listingName,
    widgetNameSpace, dispatch, manullyHideLoadder ]);

  const handleSubmit = useCallback(async () => {
    manuallyShowLoadder();
    dispatch(setErrorMessage(''));
    if (enablePreviewMode) {
      handlePreviewSubmit();
      return;
    }

    const isValid = await handleValidate();
    if (!isValid) {
      manullyHideLoadder();
      autoScrollToTheErrorField();
      return;
    }
    pubSubInstance.dispatch(`${widgetNameSpace}-submit-block`, undefined, (res) => {
      const haveFalseStatus = find(res, { status: false });
      const {
        errorCode, paymentMethodType, saveForFutureUse, status, msg,
        paymentAccountId, billingContact, receiptAccountEmail, ...restRes
      } = res.reduce((r, v) => ({ ...r, ...v }), {});

      const billingContactInfo = billingContact
        ? { ...billingContact, email: billingContact.email || receiptAccountEmail } : undefined;
      if (!haveFalseStatus) {
        pubSubInstance.dispatch(`${widgetNameSpace}-offer-qualify`, objectFilter({
          paymentMethodType,
          paymentAccountId,
          billingContact: billingContactInfo,
        }), (offersRes) => {
          const isOfferQualifyFail = !!find(offersRes, { status: false });
          if (isOfferQualifyFail) {
            manullyHideLoadder();
            dispatch(setErrorMessage({ type: 'danger', contents: 'checkout.lib.components.common.error.qualifyOfferError' }));
          } else {
            const {
              activeRefundProducts,
            } = offersRes.reduce((r, v) => ({ ...r, ...v }), {});
            const submitParams = objectFilter({
              paymentMethodType,
              paymentAccountId,
              billingContact: billingContactInfo,
              receiptAccountEmail,
              ...restRes,
              receiptEmail: receiptAccountEmail,
              products: activeRefundProducts,
              stateOf3DS: 'defaultStateOf3DS',
              saveInWallet: saveForFutureUse,
              listingName,
            });
            if (paymentMethodType === 'CREDIT_CARD') {
              dispatch(setMake3dsFlow(MAKE3DSFLOW.PRE3DSFLOW));
              dispatch(setSubmitParams(submitParams));
            } else {
              checkoutService(submitParams);
            }
          }
        });
      } else {
        manullyHideLoadder();
        if (errorCode) {
          dispatch(setErrorMessage({ type: 'danger' }));
        } else if (msg) {
          dispatch(setErrorMessage(msg));
        }
      }
    });
  }, [ widgetNameSpace, enablePreviewMode, listingName, manuallyShowLoadder,
    manullyHideLoadder, checkoutService, dispatch, handleValidate, handlePreviewSubmit ]);

  useEffect(() => {
    manuallyShowLoadder();
  }, []);

  useEffect(() => {
    const transTokenSessionItemName = `${widgetNameSpace}${TRANS_TOKEN_SESSION_ITEM_NAME_SUFFIX}`;

    if (!bankRedirectSuccess) {
      sessionStorage.removeItem(`${widgetNameSpace}-checkoutMDData`);
      sessionStorage.removeItem(`${widgetNameSpace}-checkoutActiveRefund`);
      sessionStorage.removeItem(`${widgetNameSpace}-checkoutAACross`);
      sessionStorage.removeItem(`${widgetNameSpace}-initOfferParams`);
      sessionStorage.removeItem(`${widgetNameSpace}-checkoutMagazine`);
      if (!isPaymentFlowTypeSwitched) {
        removeTransToken(transTokenSessionItemName);
      }
    }
  }, [ bankRedirectSuccess, isPaymentFlowTypeSwitched, widgetNameSpace ]);

  useEffect(() => {
    if (bankRedirectSuccess) {
      dispatch(setDefaultData(JSON.parse(sessionStorage.getItem(`${widgetNameSpace}-checkoutMDData`) || '{}').submitParams));
    }
  }, [ bankRedirectSuccess, widgetNameSpace, dispatch ]);

  useEffect(() => {
    if (bankRedirectSuccess || isPaymentFlowTypeSwitched) {
      continueCheckoutHandler();
    } else {
      initThenStartCheckoutHandler();
    }
  }, [ bankRedirectSuccess, isPaymentFlowTypeSwitched,
    continueCheckoutHandler, initThenStartCheckoutHandler ]);

  useEffect(() => {
    if (!showErrorMsgOnly && !!currentPaymentFlowType && (enablePreviewMode || totalAmount <= 0)) {
      pubSubInstance.dispatch(`${widgetNameSpace}-disable-multi-payment`, true);
    }
  }, [ showErrorMsgOnly, currentPaymentFlowType, enablePreviewMode, totalAmount, widgetNameSpace ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-submit-order`, handleSubmit);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-submit-order`, handleSubmit);
    };
  }, [ handleSubmit, widgetNameSpace ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-reload`, handleReloadData);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-reload`, handleReloadData);
    };
  }, [ handleReloadData, widgetNameSpace ]);

  const messageActions = { onUserLoginAction, handleInvalidTransactionAction };

  return (
    <React.Fragment>
      <Make3ds
        submitService={checkoutService}
        loadAllData={loadAllData}/>
      <Container className="checkout__checkout"
        showErrorMsgOnly={showErrorMsgOnly}
        errorActions={messageActions}
        isShowBanner={false}>
        {!isShowConfirmation
          ? <ContainerBody>
            <ContainerTitle titleId={'checkout.lib.components.checkout.title'}/>
            <ContainerContent>
              <ContainerContentCenter
                errorActions={messageActions}
                className="checkout__checkout__main__left">
                { SwitchPaymentFlowTypeSection && <SwitchPaymentFlowTypeSection/>}
                { showContent
                  && <CheckoutView trackingCallback={trackingCallback}
                    orderReviewActionCallback={orderReviewActionCallback}
                    handleOrderReviewCallback={handleOrderReviewCallback}/>
                }
              </ContainerContentCenter>
              <ContainerContentBar className="checkout__checkout-bar">
                <OrderSummaryContainer trackingCallback={trackingCallback}/>
              </ContainerContentBar>
            </ContainerContent>
          </ContainerBody>
          : <ContainerBody>
            {isShowConfirmPageThanksSection && <ContainerTitle title={title}
              className="checkout__paymentResult__title"
              titleId="checkout.lib.components.paymentResult.title"/>}
            <PaymentResultContainer trackingCallback={trackingCallback} />
          </ContainerBody>
        }
      </Container>
    </React.Fragment>
  );
};

Checkout.propTypes = {
  checkoutCallback: func,
  orderReviewActionCallback: func,
  handleOrderReviewCallback: func,
  onUserLoginAction: func,
  handleInvalidTransactionAction: func,
  SwitchPaymentFlowTypeSection: node,
  trackingCallback: func,
};

Checkout.defaultProps = {
  trackingCallback: noop,
  checkoutCallback: noop,
  onUserLoginAction: noop,
  handleInvalidTransactionAction: noop,
};

export default Checkout;
